import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../../firebase/firebaseConfig";
import routePaths from "../../routes/routePaths";
import AssignmentCard from "./components/AssignmentCard";
import SubmissionHistoryModal from "./components/SubmissionHistoryModal";
import StudentClassSidebar from "./components/StudentClassSidebar";
import StudentClassHeader from "./components/StudentClassHeader";
import StudentClassSchedule from "./components/StudentClassSchedule";
import StudentClassInfo from "./components/StudentClassInfo";
import StudentNotifications from "./components/StudentNotifications";

function useCurrentUser() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub && unsub();
  }, []);

  return { user, authReady };
}

function tsSeconds(ts) {
  if (!ts) return 0;
  if (typeof ts === "number") return ts > 1e12 ? Math.floor(ts / 1000) : ts;
  if (typeof ts === "string") return Math.floor(new Date(ts).getTime() / 1000);
  if (typeof ts === "object") {
    if (typeof ts.seconds === "number") return ts.seconds;
    if (typeof ts._seconds === "number") return ts._seconds;
    if (typeof ts.toDate === "function") return Math.floor(ts.toDate().getTime() / 1000);
  }
  return 0;
}

function tsToDate(ts) {
  const s = tsSeconds(ts);
  return s ? new Date(s * 1000) : null;
}

function getDisplayText(value, fallback = "—") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.vi || value.en || fallback;
  return String(value);
}

function chunkArray(arr = [], size = 10) {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, (i + 1) * size));
}

async function fetchClassWithTeachers(classId) {
  const classSnap = await getDoc(doc(db, "classes", classId));
  if (!classSnap.exists()) return null;

  let teachers = [];
  try {
    const teacherSnap = await getDocs(collection(db, "classes", classId, "teachers"));
    teachers = teacherSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("Cannot load class teachers", classId, err);
  }

  return { id: classSnap.id, ...classSnap.data(), teachers };
}

/**
 * Lấy đầy đủ các lớp mà học sinh đang tham gia.
 * Yêu cầu enrollment docs có field: uid, classId, status.
 * Fallback: nếu enrollment cũ chưa có classId thì lấy từ parent path /classes/{classId}/enrollments/{uid}.
 */
function useStudentClasses(user) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) {
      setClasses([]);
      setError("");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const qRef = query(
          collectionGroup(db, "enrollments"),
          where("uid", "==", user.uid),
          where("status", "in", ["active", "enrolled"])
        );

        const enrollmentSnap = await getDocs(qRef);
        const classIds = new Set();

        enrollmentSnap.forEach((d) => {
          const data = d.data();
          if (data.classId) {
            classIds.add(data.classId);
            return;
          }

          const classDocRef = d.ref.parent.parent;
          if (classDocRef?.id) classIds.add(classDocRef.id);
        });

        const classList = (await Promise.all([...classIds].map(fetchClassWithTeachers)))
          .filter(Boolean)
          .sort((a, b) => getDisplayText(a.name).localeCompare(getDisplayText(b.name), "vi"));

        if (!cancelled) setClasses(classList);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setClasses([]);
          setError(err?.message || "Không thể tải danh sách lớp học.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const classIds = useMemo(() => classes.map((c) => c.id), [classes]);

  return { classes, classIds, loading, error };
}

function useProgramsMap(classes) {
  const [programsMap, setProgramsMap] = useState({});

  useEffect(() => {
    const ids = [...new Set((classes || []).map((c) => c.programId).filter(Boolean))];
    if (ids.length === 0) {
      setProgramsMap({});
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = {};
        await Promise.all(
          ids.map(async (id) => {
            const snap = await getDoc(doc(db, "programs", id));
            if (snap.exists()) result[id] = { id: snap.id, ...snap.data() };
          })
        );
        if (!cancelled) setProgramsMap(result);
      } catch (err) {
        console.warn("Cannot load programs", err);
        if (!cancelled) setProgramsMap({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [classes]);

  return programsMap;
}

/** Lấy assignments đã publish thuộc các lớp của học sinh. */
function useAssignments(classIds) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const classIdsKey = useMemo(() => (classIds || []).filter(Boolean).sort().join("|"), [classIds]);

  useEffect(() => {
    const ids = classIdsKey ? classIdsKey.split("|").filter(Boolean) : [];

    if (ids.length === 0) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    setItems([]);
    setLoading(true);

    const chunks = chunkArray(ids, 10);
    const chunkStores = new Map();

    const unsubscribes = chunks.map((chunkIds, index) => {
      const qRef = query(
        collection(db, "assignments"),
        where("classId", "in", chunkIds),
        where("published", "==", true)
      );

      return onSnapshot(
        qRef,
        (snap) => {
          chunkStores.set(
            index,
            snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          );

          const merged = Array.from(chunkStores.values()).flat();
          const unique = Array.from(new Map(merged.map((a) => [a.id, a])).values());
          setItems(unique);
          setLoading(false);
        },
        (err) => {
          console.error("Cannot load assignments", err);
          setLoading(false);
        }
      );
    });

    return () => unsubscribes.forEach((u) => u && u());
  }, [classIdsKey]);

  return { assignments: items, loading };
}


/** Lấy thông báo đã publish theo các lớp của học sinh. */
function useClassNotifications(classIds, user) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const uid = user?.uid || "";
  const classIdsKey = useMemo(() => (classIds || []).filter(Boolean).sort().join("|"), [classIds]);

  useEffect(() => {
    const ids = classIdsKey ? classIdsKey.split("|").filter(Boolean) : [];

    if (!uid || ids.length === 0) {
      setNotifications([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const chunks = chunkArray(ids, 10);
    const chunkStores = new Map();

    const unsubscribes = chunks.map((chunkIds, index) => {
      const qRef = query(
        collection(db, "class_notifications"),
        where("classId", "in", chunkIds),
        where("published", "==", true)
      );

      return onSnapshot(
        qRef,
        (snap) => {
          const items = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((item) => {
              const targetMode = item.audience || "all";
              const targetIds = item.studentIds || [];
              return targetMode === "all" || targetIds.includes(uid);
            });

          chunkStores.set(index, items);

          const merged = Array.from(chunkStores.values()).flat();
          const unique = Array.from(new Map(merged.map((n) => [n.id, n])).values());
          setNotifications(unique);
          setLoading(false);
        },
        (err) => {
          console.error("Cannot load class notifications", err);
          setLoading(false);
        }
      );
    });

    return () => unsubscribes.forEach((u) => u && u());
  }, [classIdsKey, uid]);

  return { notifications, loading };
}

/** Realtime trạng thái đã đọc của thông báo.
 * Dùng subcollection class_notifications/{notificationId}/reads/{uid}
 * để học sinh tự mark read mà không phải update document thông báo chính.
 */
function useNotificationReads(notifications, user) {
  const [readsMap, setReadsMap] = useState({});
  const uid = user?.uid || "";
  const notificationIdsKey = useMemo(
    () => (notifications || []).map((n) => n.id).filter(Boolean).sort().join("|"),
    [notifications]
  );

  useEffect(() => {
    const ids = notificationIdsKey ? notificationIdsKey.split("|").filter(Boolean) : [];

    if (!uid || ids.length === 0) {
      setReadsMap({});
      return undefined;
    }

    const unsubscribes = ids.map((notificationId) => {
      const readRef = doc(db, "class_notifications", notificationId, "reads", uid);
      return onSnapshot(
        readRef,
        (snap) => {
          setReadsMap((prev) => {
            const next = { ...prev };
            if (snap.exists()) next[notificationId] = { id: snap.id, ...snap.data() };
            else delete next[notificationId];
            return next;
          });
        },
        (err) => console.warn("Cannot load notification read state", notificationId, err)
      );
    });

    return () => unsubscribes.forEach((u) => u && u());
  }, [notificationIdsKey, uid]);

  return readsMap;
}


/** Lấy histories của chính user. */
function useMyHistories(user) {
  const [histories, setHistories] = useState([]);

  useEffect(() => {
    if (!user) {
      setHistories([]);
      return;
    }

    const qRef = query(
      collection(db, "test_histories"),
      where("userId", "==", user.uid),
      orderBy("submittedAt", "desc")
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => setHistories(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      console.error
    );

    return () => unsub && unsub();
  }, [user]);

  return histories;
}

function getPercent(h) {
  if (typeof h?.percent === "number" && Number.isFinite(h.percent)) return Math.round(h.percent);

  const correct = typeof h?.score?.correct === "number" ? h.score.correct : null;
  const total = typeof h?.score?.total === "number" ? h.score.total : null;

  if (typeof correct === "number" && typeof total === "number" && total > 0) {
    return Math.round((correct / total) * 100);
  }

  return null;
}

function computeStatus(assignment, myHistories) {
  const list = myHistories
    .filter((h) => h.assignmentId === assignment.id)
    .sort((a, b) => tsSeconds(b.submittedAt || b.updatedAt) - tsSeconds(a.submittedAt || a.updatedAt));

  const attempts = list.length;
  const submitted = list.filter((h) => {
    const st = (h.status || "").toLowerCase();
    return st === "submitted" || st === "finished" || !!h.submittedAt;
  });

  const latest = list[0] || null;

  let best = null;
  if (submitted.length > 0) {
    best = submitted.reduce((acc, cur) => {
      const pa = getPercent(acc) ?? -1;
      const pb = getPercent(cur) ?? -1;
      if (pb > pa) return cur;
      if (pb === pa) return tsSeconds(cur.submittedAt) >= tsSeconds(acc.submittedAt) ? cur : acc;
      return acc;
    }, submitted[0]);
  }

  const bestPercent = getPercent(best);

  let label = "Chưa làm";
  let type = "idle";

  if (attempts > 0 && submitted.length === 0) {
    label = "Đang làm dở";
    type = "inprogress";
  } else if (submitted.length > 0) {
    label = `Đã nộp (${submitted.length} lần)`;
    type = "submitted";
  }

  let canStart = true;
  let blockReason = "";

  const deadline = tsToDate(assignment.deadline);
  const over = deadline ? Date.now() > new Date(deadline).setHours(23, 59, 59, 999) : false;

  if (over && !assignment.allowLate) {
    canStart = false;
    blockReason = "Đã quá hạn và không cho nộp trễ.";
  }

  const maxAttempts = Number(assignment.maxAttempts || 1);
  if (canStart && maxAttempts > 0 && attempts >= maxAttempts) {
    canStart = false;
    blockReason = `Đã vượt quá số lần thử (${attempts}/${maxAttempts}).`;
  }

  return { label, type, latest, best, bestPercent, attempts, canStart, blockReason };
}

export default function StudentAssignments() {
  const { user, authReady } = useCurrentUser();
  const { classes, classIds, loading: loadingClasses, error: classesError } = useStudentClasses(user);
  const programsMap = useProgramsMap(classes);
  const { assignments, loading: loadingAssignments } = useAssignments(classIds);
  const { notifications, loading: loadingNotifications } = useClassNotifications(classIds, user);
  const notificationReads = useNotificationReads(notifications, user);
  const histories = useMyHistories(user);
  const navigate = useNavigate();

  const [selectedClassId, setSelectedClassId] = useState("all");
  const [activeTab, setActiveTab] = useState("assignments");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedForHistory, setSelectedForHistory] = useState(null);

  useEffect(() => {
    setSelectedClassId((prev) => {
      if (classes.length === 1) return classes[0].id;
      if (classes.length > 1) return classes.some((c) => c.id === prev) ? prev : "all";
      return prev;
    });
  }, [classes]);

  const selectedClass = selectedClassId === "all" ? null : classes.find((c) => c.id === selectedClassId) || null;

  const enriched = useMemo(() => {
    return assignments
      .map((a) => ({
        ...a,
        __status: computeStatus(a, histories),
        __class: classes.find((c) => c.id === a.classId) || null,
      }))
      .sort((x, y) => {
        const dx = tsSeconds(x.deadline) || Number.MAX_SAFE_INTEGER;
        const dy = tsSeconds(y.deadline) || Number.MAX_SAFE_INTEGER;
        return dx - dy;
      });
  }, [assignments, histories, classes]);

  const visibleAssignments = useMemo(() => {
    return enriched.filter((a) => {
      const title = getDisplayText(a.title, "");
      const description = getDisplayText(a.description, "");
      const okClass = selectedClassId === "all" || a.classId === selectedClassId;
      const okSearch = !search || `${title} ${description}`.toLowerCase().includes(search.toLowerCase());
      const st = a.__status?.type || "idle";
      const okStatus = statusFilter === "all" || statusFilter === st;
      return okClass && okSearch && okStatus;
    });
  }, [enriched, selectedClassId, search, statusFilter]);

  const groupedAssignments = useMemo(() => {
    return classes.map((cls) => ({
      classInfo: cls,
      assignments: visibleAssignments.filter((a) => a.classId === cls.id),
    })).filter((group) => group.assignments.length > 0);
  }, [classes, visibleAssignments]);

  const assignmentCounts = useMemo(() => {
    const counts = { all: enriched.length };
    enriched.forEach((a) => {
      counts[a.classId] = (counts[a.classId] || 0) + 1;
    });
    return counts;
  }, [enriched]);

  const stats = useMemo(() => {
    return {
      totalClasses: classes.length,
      totalAssignments: enriched.length,
      pending: enriched.filter((a) => a.__status?.type === "idle" || a.__status?.type === "inprogress").length,
      submitted: enriched.filter((a) => a.__status?.type === "submitted").length,
      unreadNotifications: notifications.filter((n) => !notificationReads[n.id] && !n.readBy?.[user?.uid]).length,
    };
  }, [classes, enriched, notifications, notificationReads, user?.uid]);

  const handleStartOrResume = (assignment) => {
    if (assignment.startPath) {
      navigate(assignment.startPath);
      return;
    }

    if (assignment.testId) {
      navigate(routePaths.helpers.testPage(assignment.testId, assignment.id, assignment.classId));
      return;
    }

    navigate(`/assignments/${assignment.id}`);
  };

  const handleOpenResult = (testId, historyId) => {
    navigate(routePaths.helpers.testPageResult(testId, historyId));
  };

  const handleMarkNotificationRead = async (notification) => {
    if (!user?.uid || !notification?.id) return;

    await setDoc(
      doc(db, "class_notifications", notification.id, "reads", user.uid),
      {
        uid: user.uid,
        notificationId: notification.id,
        classId: notification.classId || "",
        readAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const handleChangeClass = (classId) => {
    setSelectedClassId(classId);
    setActiveTab("assignments");
  };

  if (!authReady) {
    return <div className="max-w-6xl mx-auto p-4 text-center text-slate-600">Đang kiểm tra đăng nhập...</div>;
  }

  if (!user) {
    return (
      <div className="max-w-5xl mx-auto p-4 text-center">
        <h2 className="text-xl font-semibold mb-2">Vui lòng đăng nhập</h2>
        <p className="opacity-80">Bạn cần đăng nhập để xem danh sách lớp học và bài tập/kiểm tra của mình.</p>
      </div>
    );
  }

  const loading = loadingClasses || loadingAssignments || loadingNotifications;

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lớp học & bài tập của tôi</h1>
          <p className="text-slate-600 mt-1">Theo dõi lớp đang học, lịch học, thông tin lớp và bài tập/kiểm tra được giao.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="rounded-xl border bg-white px-4 py-3 text-center">
            <div className="text-xl font-bold text-blue-700">{stats.totalClasses}</div>
            <div className="text-xs text-slate-500">Lớp</div>
          </div>
          <div className="rounded-xl border bg-white px-4 py-3 text-center">
            <div className="text-xl font-bold text-blue-700">{stats.totalAssignments}</div>
            <div className="text-xs text-slate-500">Bài giao</div>
          </div>
          <div className="rounded-xl border bg-white px-4 py-3 text-center">
            <div className="text-xl font-bold text-amber-600">{stats.pending}</div>
            <div className="text-xs text-slate-500">Cần làm</div>
          </div>
          <div className="rounded-xl border bg-white px-4 py-3 text-center">
            <div className="text-xl font-bold text-emerald-600">{stats.submitted}</div>
            <div className="text-xs text-slate-500">Đã nộp</div>
          </div>
          <div className="rounded-xl border bg-white px-4 py-3 text-center">
            <div className="text-xl font-bold text-purple-600">{stats.unreadNotifications}</div>
            <div className="text-xs text-slate-500">Thông báo mới</div>
          </div>
        </div>
      </div>

      {classesError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {classesError}
        </div>
      )}

      {loading && (
        <div className="rounded-xl border bg-white p-4 text-sm text-slate-600">Đang tải dữ liệu...</div>
      )}

      {!loadingClasses && classes.length === 0 ? (
        <div className="rounded-2xl border bg-white p-8 text-center text-slate-600">
          Bạn chưa được thêm vào lớp học nào. Vui lòng liên hệ giáo viên hoặc quản trị viên.
        </div>
      ) : (
        <div className="grid lg:grid-cols-[300px_1fr] gap-5 items-start">
          <div className="space-y-3">
            <div className="lg:hidden rounded-2xl border bg-white p-3">
              <label className="block text-sm font-medium text-slate-600 mb-2">Chọn lớp</label>
              <select
                value={selectedClassId}
                onChange={(e) => handleChangeClass(e.target.value)}
                className="w-full rounded-xl border px-3 py-2 bg-white"
              >
                <option value="all">Tất cả lớp</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>{getDisplayText(cls.name, cls.id)}</option>
                ))}
              </select>
            </div>

            <div className="hidden lg:block">
              <StudentClassSidebar
                classes={classes}
                selectedClassId={selectedClassId}
                onSelect={handleChangeClass}
                assignmentCounts={assignmentCounts}
              />
            </div>
          </div>

          <main className="space-y-5">
            <StudentClassHeader
              selectedClass={selectedClass}
              program={selectedClass ? programsMap[selectedClass.programId] : null}
              assignmentCount={visibleAssignments.length}
            />

            <div className="rounded-2xl border bg-white p-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("assignments")}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${activeTab === "assignments" ? "bg-blue-600 text-white" : "hover:bg-slate-100 text-slate-700"}`}
              >
                Bài tập/kiểm tra
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("notifications")}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${activeTab === "notifications" ? "bg-blue-600 text-white" : "hover:bg-slate-100 text-slate-700"}`}
              >
                Thông báo{stats.unreadNotifications ? ` (${stats.unreadNotifications})` : ""}
              </button>
              <button
                type="button"
                onClick={() => selectedClass && setActiveTab("schedule")}
                disabled={!selectedClass}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${activeTab === "schedule" ? "bg-blue-600 text-white" : "hover:bg-slate-100 text-slate-700"} ${!selectedClass ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Lịch học
              </button>
              <button
                type="button"
                onClick={() => selectedClass && setActiveTab("info")}
                disabled={!selectedClass}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${activeTab === "info" ? "bg-blue-600 text-white" : "hover:bg-slate-100 text-slate-700"} ${!selectedClass ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Thông tin lớp
              </button>
            </div>

            {activeTab === "assignments" && (
              <section className="rounded-2xl border bg-white p-4 md:p-5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                  <h2 className="text-xl font-bold text-slate-900">Bài tập/kiểm tra</h2>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Tìm theo tiêu đề..."
                      className="w-full sm:w-72 px-3 py-2 rounded-xl border outline-none bg-white"
                    />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 rounded-xl border bg-white"
                    >
                      <option value="all">Tất cả trạng thái</option>
                      <option value="idle">Chưa làm</option>
                      <option value="inprogress">Đang làm dở</option>
                      <option value="submitted">Đã nộp</option>
                    </select>
                  </div>
                </div>

                {visibleAssignments.length === 0 ? (
                  <div className="rounded-xl border border-dashed p-8 text-center text-slate-500">
                    Không có bài tập/kiểm tra phù hợp.
                  </div>
                ) : selectedClassId === "all" ? (
                  <div className="space-y-6">
                    {groupedAssignments.map((group) => (
                      <div key={group.classInfo.id}>
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <h3 className="font-bold text-slate-900">{getDisplayText(group.classInfo.name)}</h3>
                          <button
                            type="button"
                            onClick={() => setSelectedClassId(group.classInfo.id)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-700"
                          >
                            Xem lớp này
                          </button>
                        </div>
                        <div className="space-y-3">
                          {group.assignments.map((assignment) => (
                            <AssignmentCard
                              key={assignment.id}
                              assignment={assignment}
                              status={assignment.__status}
                              classInfo={assignment.__class}
                              onStart={() => handleStartOrResume(assignment)}
                              onOpenHistory={() => setSelectedForHistory(assignment)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleAssignments.map((assignment) => (
                      <AssignmentCard
                        key={assignment.id}
                        assignment={assignment}
                        status={assignment.__status}
                        classInfo={assignment.__class}
                        onStart={() => handleStartOrResume(assignment)}
                        onOpenHistory={() => setSelectedForHistory(assignment)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {activeTab === "notifications" && (
              <StudentNotifications
                notifications={notifications}
                classes={classes}
                user={user}
                readsMap={notificationReads}
                selectedClassId={selectedClassId}
                onMarkRead={handleMarkNotificationRead}
              />
            )}

            {activeTab === "schedule" && selectedClass && (
              <StudentClassSchedule schedule={selectedClass.schedule} />
            )}

            {activeTab === "info" && selectedClass && (
              <StudentClassInfo classInfo={selectedClass} program={programsMap[selectedClass.programId]} />
            )}
          </main>
        </div>
      )}

      {selectedForHistory && (
        <SubmissionHistoryModal
          assignment={selectedForHistory}
          histories={histories.filter((h) => h.assignmentId === selectedForHistory.id)}
          onClose={() => setSelectedForHistory(null)}
          onOpenResult={handleOpenResult}
        />
      )}
    </div>
  );
}
